import axios from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import { trackPromise } from 'react-promise-tracker';
import { useHistory, useLocation } from 'react-router-dom';

import { Toast } from 'constants/toasts';
import { ROUTES } from 'constants/routes';
import { PROMISES_AREA } from 'constants/promises-area';
import { errorMessage, UserErrorMessages } from 'constants/pop-up-messages';

import { signUp } from 'services/user.service';
import { storageService } from 'services/storage/storage';
import { useUpdateState } from 'services/hooks/useUpdateState';
import { useAppDispatch, useAppSelector } from 'services/hooks/redux';

import { loginUser } from 'store/reducers/user.slice';

import { initialSignUpState } from './form.constants';
import { formatSignInData, getPreviousAuthUrl } from 'services/utils';

export const useSignUpState = () => {
  const { state, updateState } = useUpdateState(initialSignUpState);
  const [isShowFields, setIsShowFields] = useState(false);
  const { replace } = useHistory();
  const location = useLocation<ILocationState>();

  const isNTU = location.pathname === ROUTES.ntuSignUp;

  const appUser = useAppSelector((redux) => redux.user);

  const dispatch = useAppDispatch();
  const [isPasswordShown, setIsPasswordShown] = useState(false);

  useEffect(() => {
    const token = storageService.getToken();

    if (token && appUser) {
      return location.state
        ? replace(getPreviousAuthUrl(location.state?.from))
        : replace(ROUTES.main);
    }
  }, [appUser]);

  const redirectToSignIn = () => {
    replace(isNTU ? ROUTES.ntuSignIn : ROUTES.signIn, location.state);
  };

  const switchShowFields = () => setIsShowFields((prev) => !prev);

  const signUpHandler = async () => {
    try {
      const { data } = await trackPromise(
        signUp({
          ...formatSignInData(state),
          firstName: state.firstName.trim(),
          lastName: state.lastName.trim(),
          isNtu: isNTU,
        }),
        PROMISES_AREA.authSignUp
      );

      Toast.fire({
        icon: 'success',
        title: 'Signed up successfully',
      });

      dispatch(loginUser(data.user));

      storageService.setToken(data.token, false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          return UserErrorMessages['409'].fire();
        }

        return errorMessage(error?.response?.data.message).fire();
      }
    }
  };

  const onChangeUser = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateState({
      [name]: ['email', 'password'].includes(name) ? value.trim() : value,
    });
  };
  const toggleShowPassword = () => {
    setIsPasswordShown((prev) => !prev);
  };

  const isExpanded = !isNTU || isShowFields;

  return {
    isNTU,
    onChangeUser,
    signUpHandler,
    redirectToSignIn,
    switchShowFields,
    state,
    isPasswordShown,
    toggleShowPassword,
    isExpanded,
  };
};
